import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  MoreHorizontal,
  Plus,
  Workflow,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { ApiError } from "@/api/client";
import {
  createAssignmentRule,
  deleteAssignmentRule,
  listAssignmentRules,
  patchAssignmentRule,
} from "@/api/assignment-rules";
import { listGroups } from "@/api/groups";
import type {
  AssignmentRuleOut,
  CreateAssignmentRuleRequest,
  GroupOut,
  MailboxResponse,
  UserSummary,
} from "@/api/schemas";
import { listOrgUsers } from "@/api/users";
import { apiFetch } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TableBodySkeleton } from "@/components/ui/skeleton";
import { TBody, TD, TH, THead, TR, Table } from "@/components/ui/table";

const RULES_QUERY = ["assignment-rules"] as const;
const MAILBOXES_QUERY = ["mailboxes"] as const;
const GROUPS_QUERY = ["groups"] as const;
const ORG_USERS_QUERY = ["org-users"] as const;

function AssignmentRulesAdminPage() {
  const { data: rules, isLoading } = useQuery({
    queryKey: RULES_QUERY,
    queryFn: () => listAssignmentRules(),
  });
  const { data: mailboxes } = useQuery({
    queryKey: MAILBOXES_QUERY,
    queryFn: () => apiFetch<MailboxResponse[]>("/api/mailboxes"),
  });
  const { data: groups } = useQuery({
    queryKey: GROUPS_QUERY,
    queryFn: listGroups,
  });
  const { data: users } = useQuery({
    queryKey: ORG_USERS_QUERY,
    queryFn: listOrgUsers,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AssignmentRuleOut | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AssignmentRuleOut | null>(
    null,
  );

  const mailboxLookup = useMemo(() => {
    const map = new Map<string, string>();
    (mailboxes ?? []).forEach((m) => map.set(m.id, m.email_address));
    return map;
  }, [mailboxes]);
  const groupLookup = useMemo(() => {
    const map = new Map<string, string>();
    (groups ?? []).forEach((g) => map.set(g.id, g.name));
    return map;
  }, [groups]);
  const userLookup = useMemo(() => {
    const map = new Map<string, string>();
    (users ?? []).forEach((u) => map.set(u.id, u.name));
    return map;
  }, [users]);

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-medium tracking-tight">
            Assignment rules
          </h1>
          <p className="mt-1 text-sm text-text-muted">
            Rules route incoming tickets on mailboxes set to{" "}
            <span className="font-mono">rule_based</span> mode. Highest-priority
            match wins; tickets that match nothing fall through to round-robin.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          Create rule
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-surface">
        {isLoading ? (
          <Table>
            <THead>
              <TR>
                <TH>Priority</TH>
                <TH>Name</TH>
                <TH>Mailbox</TH>
                <TH>Conditions</TH>
                <TH>Target</TH>
                <TH>Active</TH>
                <TH className="w-10" />
              </TR>
            </THead>
            <TableBodySkeleton rows={4} cells={7} />
          </Table>
        ) : !rules || rules.length === 0 ? (
          <EmptyState />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Priority</TH>
                <TH>Name</TH>
                <TH>Mailbox</TH>
                <TH>Conditions</TH>
                <TH>Target</TH>
                <TH>Active</TH>
                <TH className="w-10" />
              </TR>
            </THead>
            <TBody>
              {rules.map((r) => (
                <TR key={r.id}>
                  <TD className="font-mono text-xs text-text-muted">
                    {r.priority}
                  </TD>
                  <TD className="font-medium">{r.name}</TD>
                  <TD className="text-xs text-text-muted">
                    {r.mailbox_id
                      ? mailboxLookup.get(r.mailbox_id) ?? r.mailbox_id
                      : "All mailboxes"}
                  </TD>
                  <TD className="text-xs text-text-muted">
                    <ConditionsSummary conditions={r.conditions} />
                  </TD>
                  <TD className="text-xs">
                    <TargetSummary
                      action={r.action}
                      groupLookup={groupLookup}
                      userLookup={userLookup}
                    />
                  </TD>
                  <TD>{r.is_active ? <ActiveBadge /> : <InactiveBadge />}</TD>
                  <TD>
                    <RowActionMenu
                      onEdit={() => setEditTarget(r)}
                      onDelete={() => setDeleteTarget(r)}
                    />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>

      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditDialog
        rule={editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
      />
      <DeleteDialog
        rule={deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-surface-subtle">
        <Workflow className="h-4 w-4 text-text-muted" />
      </div>
      <div className="text-sm font-medium">No rules yet</div>
      <div className="mt-1 text-xs text-text-muted">
        Click <span className="font-mono">Create rule</span> to add one.
      </div>
    </div>
  );
}

function ActiveBadge() {
  return (
    <span className="inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-600">
      active
    </span>
  );
}
function InactiveBadge() {
  return (
    <span className="inline-flex items-center rounded-full bg-surface-subtle px-2 py-0.5 text-[10px] font-medium text-text-muted">
      inactive
    </span>
  );
}

function ConditionsSummary({
  conditions,
}: {
  conditions: Record<string, unknown>;
}) {
  const parts: string[] = [];
  if (conditions.from_contains) parts.push(`from~"${conditions.from_contains}"`);
  if (conditions.from_domain) parts.push(`@${conditions.from_domain}`);
  if (conditions.subject_contains)
    parts.push(`subj~"${conditions.subject_contains}"`);
  if (conditions.contact_is_vip) parts.push("VIP");
  const tagIds = conditions.tag_ids as string[] | undefined;
  if (tagIds && tagIds.length > 0) parts.push(`${tagIds.length} tag(s)`);
  if (parts.length === 0) return <span>any</span>;
  return <span>{parts.join(", ")}</span>;
}

function TargetSummary({
  action,
  groupLookup,
  userLookup,
}: {
  action: Record<string, unknown>;
  groupLookup: Map<string, string>;
  userLookup: Map<string, string>;
}) {
  if (action.assignee_user_id) {
    const id = String(action.assignee_user_id);
    return (
      <span>
        user:{" "}
        <span className="font-medium">{userLookup.get(id) ?? id}</span>
      </span>
    );
  }
  if (action.group_id) {
    const id = String(action.group_id);
    return (
      <span>
        group:{" "}
        <span className="font-medium">{groupLookup.get(id) ?? id}</span>
      </span>
    );
  }
  return <span className="text-text-muted">—</span>;
}

function RowActionMenu({
  onEdit,
  onDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label="Actions">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onSelect={onEdit}>Edit</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={onDelete}>
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type FormValues = {
  name: string;
  mailbox_id: string;  // empty string = "All mailboxes"
  priority: number;
  is_active: boolean;
  // conditions
  from_contains: string;
  from_domain: string;
  subject_contains: string;
  contact_is_vip: boolean;
  // action — radio: "user" | "group"
  action_kind: "user" | "group";
  action_user_id: string;
  action_group_id: string;
};

function defaultFormValues(): FormValues {
  return {
    name: "",
    mailbox_id: "",
    priority: 0,
    is_active: true,
    from_contains: "",
    from_domain: "",
    subject_contains: "",
    contact_is_vip: false,
    action_kind: "user",
    action_user_id: "",
    action_group_id: "",
  };
}

function valuesFromRule(rule: AssignmentRuleOut): FormValues {
  const c = (rule.conditions ?? {}) as {
    from_contains?: string;
    from_domain?: string;
    subject_contains?: string;
    contact_is_vip?: boolean;
  };
  const a = (rule.action ?? {}) as {
    assignee_user_id?: string;
    group_id?: string;
  };
  return {
    name: rule.name,
    mailbox_id: rule.mailbox_id ?? "",
    priority: rule.priority,
    is_active: rule.is_active,
    from_contains: c.from_contains ?? "",
    from_domain: c.from_domain ?? "",
    subject_contains: c.subject_contains ?? "",
    contact_is_vip: c.contact_is_vip ?? false,
    action_kind: a.group_id ? "group" : "user",
    action_user_id: a.assignee_user_id ?? "",
    action_group_id: a.group_id ?? "",
  };
}

function buildPayload(v: FormValues): CreateAssignmentRuleRequest {
  const conditions: Record<string, unknown> = {};
  const fc = v.from_contains.trim();
  const fd = v.from_domain.trim();
  const sc = v.subject_contains.trim();
  if (fc) conditions.from_contains = fc;
  if (fd) conditions.from_domain = fd.toLowerCase();
  if (sc) conditions.subject_contains = sc;
  if (v.contact_is_vip) conditions.contact_is_vip = true;

  const action: Record<string, unknown> = {};
  if (v.action_kind === "user" && v.action_user_id) {
    action.assignee_user_id = v.action_user_id;
  } else if (v.action_kind === "group" && v.action_group_id) {
    action.group_id = v.action_group_id;
  }

  return {
    name: v.name.trim(),
    mailbox_id: v.mailbox_id || null,
    priority: v.priority,
    is_active: v.is_active,
    conditions: conditions as CreateAssignmentRuleRequest["conditions"],
    action: action as CreateAssignmentRuleRequest["action"],
  };
}

function CreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const form = useForm<FormValues>({ defaultValues: defaultFormValues() });

  const mutation = useMutation({
    mutationFn: (v: FormValues) => createAssignmentRule(buildPayload(v)),
    onSuccess: (created) => {
      toast.success(`Created ${created.name}`);
      void qc.invalidateQueries({ queryKey: RULES_QUERY });
      onOpenChange(false);
      form.reset(defaultFormValues());
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not create rule");
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) form.reset(defaultFormValues());
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create assignment rule</DialogTitle>
          <DialogDescription>
            Conditions are AND-ed: every set field must match. Empty rule
            matches everything.
          </DialogDescription>
        </DialogHeader>
        <RuleForm form={form} mutation={mutation} submitLabel="Create" />
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({
  rule,
  onOpenChange,
}: {
  rule: AssignmentRuleOut | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const form = useForm<FormValues>({
    values: rule ? valuesFromRule(rule) : undefined,
  });

  const mutation = useMutation({
    mutationFn: (v: FormValues) => {
      if (!rule) throw new Error("no rule");
      return patchAssignmentRule(rule.id, buildPayload(v));
    },
    onSuccess: (updated) => {
      toast.success(`Updated ${updated.name}`);
      void qc.invalidateQueries({ queryKey: RULES_QUERY });
      onOpenChange(false);
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not update rule");
    },
  });

  if (!rule) return null;

  return (
    <Dialog open={!!rule} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit assignment rule</DialogTitle>
          <DialogDescription className="text-xs">{rule.name}</DialogDescription>
        </DialogHeader>
        <RuleForm form={form} mutation={mutation} submitLabel="Save" />
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({
  rule,
  onOpenChange,
}: {
  rule: AssignmentRuleOut | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => {
      if (!rule) throw new Error("no rule");
      return deleteAssignmentRule(rule.id);
    },
    onSuccess: () => {
      toast.success(`Deleted ${rule?.name ?? "rule"}`);
      void qc.invalidateQueries({ queryKey: RULES_QUERY });
      onOpenChange(false);
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not delete rule");
    },
  });

  if (!rule) return null;

  return (
    <Dialog open={!!rule} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete rule</DialogTitle>
          <DialogDescription>
            Delete <strong>{rule.name}</strong>? This is permanent. Existing
            ticket assignments are unaffected.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RuleForm({
  form,
  mutation,
  submitLabel,
}: {
  form: ReturnType<typeof useForm<FormValues>>;
  mutation: { mutate: (v: FormValues) => void; isPending: boolean };
  submitLabel: string;
}) {
  const { data: mailboxes } = useQuery({
    queryKey: MAILBOXES_QUERY,
    queryFn: () => apiFetch<MailboxResponse[]>("/api/mailboxes"),
  });
  const { data: groups } = useQuery({
    queryKey: GROUPS_QUERY,
    queryFn: listGroups,
  });
  const { data: users } = useQuery({
    queryKey: ORG_USERS_QUERY,
    queryFn: listOrgUsers,
  });

  const actionKind = form.watch("action_kind");
  const actionUserId = form.watch("action_user_id");
  const actionGroupId = form.watch("action_group_id");
  const actionInvalid =
    (actionKind === "user" && !actionUserId) ||
    (actionKind === "group" && !actionGroupId);

  return (
    <form
      onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
      className="flex flex-col gap-3"
    >
      <FormField label="Name">
        <Input
          {...form.register("name", {
            required: true,
            minLength: 1,
            maxLength: 120,
          })}
          placeholder="VIP customers → senior team"
          autoFocus
        />
      </FormField>

      <div className="grid grid-cols-2 gap-3">
        <FormField label="Mailbox">
          <NativeSelect
            value={form.watch("mailbox_id")}
            onChange={(v) => form.setValue("mailbox_id", v)}
            options={[
              { value: "", label: "All mailboxes" },
              ...(mailboxes ?? []).map((m) => ({
                value: m.id,
                label: m.email_address,
              })),
            ]}
          />
        </FormField>
        <FormField label="Priority" hint="Higher wins (0–1000)">
          <Input
            type="number"
            {...form.register("priority", {
              valueAsNumber: true,
              min: 0,
              max: 1000,
            })}
          />
        </FormField>
      </div>

      <fieldset className="flex flex-col gap-2 rounded border border-border p-3">
        <legend className="px-1 text-xs font-medium text-text-muted">
          Conditions (AND)
        </legend>
        <FormField label="From contains">
          <Input
            {...form.register("from_contains", { maxLength: 200 })}
            placeholder="@vip-client.com"
          />
        </FormField>
        <FormField label="From domain (exact)">
          <Input
            {...form.register("from_domain", { maxLength: 200 })}
            placeholder="enterprise.com"
          />
        </FormField>
        <FormField label="Subject contains">
          <Input
            {...form.register("subject_contains", { maxLength: 200 })}
            placeholder="urgent"
          />
        </FormField>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            {...form.register("contact_is_vip")}
            className="h-4 w-4"
          />
          <span>Contact is VIP</span>
        </label>
      </fieldset>

      <fieldset className="flex flex-col gap-2 rounded border border-border p-3">
        <legend className="px-1 text-xs font-medium text-text-muted">
          Action
        </legend>
        <div className="flex gap-4 text-xs">
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              value="user"
              checked={actionKind === "user"}
              onChange={() => form.setValue("action_kind", "user")}
            />
            <span>Assign to user</span>
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              value="group"
              checked={actionKind === "group"}
              onChange={() => form.setValue("action_kind", "group")}
            />
            <span>Assign to group</span>
          </label>
        </div>
        {actionKind === "user" ? (
          <NativeSelect
            value={form.watch("action_user_id")}
            onChange={(v) => form.setValue("action_user_id", v)}
            options={[
              { value: "", label: "Pick a user…" },
              ...(users ?? [])
                .filter((u: UserSummary) => u.is_active)
                .map((u: UserSummary) => ({
                  value: u.id,
                  label: `${u.name} (${u.email})`,
                })),
            ]}
          />
        ) : (
          <NativeSelect
            value={form.watch("action_group_id")}
            onChange={(v) => form.setValue("action_group_id", v)}
            options={[
              { value: "", label: "Pick a group…" },
              ...(groups ?? []).map((g: GroupOut) => ({
                value: g.id,
                label: g.name,
              })),
            ]}
          />
        )}
      </fieldset>

      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          {...form.register("is_active")}
          className="h-4 w-4"
        />
        <span>Active</span>
      </label>

      <DialogFooter>
        <Button type="submit" disabled={mutation.isPending || actionInvalid}>
          {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}

function NativeSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 rounded border border-border bg-bg px-2 text-xs"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function FormField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
  );
}

export const Route = createFileRoute("/_auth/settings/assignment-rules")({
  component: AssignmentRulesAdminPage,
});
